"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Context = void 0;
const Failable_1 = require("./Failable");
const ChainedError_1 = require("./ChainedError");
const TIMEOUT_MS = 20 * 1000;
const DEFAULT_RETRY_CONFIG = {
    boundWait: false,
    timeoutMS: TIMEOUT_MS,
    maxTimeoutMS: TIMEOUT_MS * 10,
    maxTries: 5,
    logEnabled: false,
};
;
const DEFAULT_SAFE_STRING_OPTIONS = {
    longstring: 'full',
    longstringlen: 128,
    null: '<null>',
    indent: undefined,
};
class Context {
    constructor(label = '', retryCfg = DEFAULT_RETRY_CONFIG) {
        this.label = label;
        this.retryConfig = retryCfg;
    }
    newCtx(newLabel) {
        const newlabel = this.label + '|' + newLabel;
        return new Context(newlabel, this.retryConfig);
    }
    getRetryConfig() {
        return this.retryConfig;
    }
    setRetryConfig(cfg) {
        this.retryConfig = {
            ...this.retryConfig,
            ...cfg
        };
    }
    prefix() {
        function pad(n, width = 2) {
            const s = String(n);
            return s.padStart(width, '0');
        }
        if (Context.ENABLE_TIMESTAMP_PREFIX) {
            const now = new Date();
            const hr = pad(now.getHours());
            const min = pad(now.getMinutes());
            const sec = pad(now.getSeconds());
            const milli = pad(now.getMilliseconds(), 3);
            return `${hr}:${min}:${sec}.${milli} `;
        }
        return '';
    }
    log(message, ...opt) {
        /* eslint-disable-next-line no-console */
        console.log(this.prefix() + this.label + ': ' + message, ...opt);
    }
    newError(message, ...opt) {
        return new Error(this.format(message, ...opt));
    }
    safeErrorMessage(err) {
        if (err instanceof Error) {
            return err.message;
        }
        else if (typeof err === 'string') {
            return `"${err}"`;
        }
        else if (typeof err === 'undefined') {
            return '<undefined>';
        }
        else if (err === null) {
            return '<null>';
        }
        return `<error: ${err}`;
    }
    chainError(err, message, ...opt) {
        let realErr;
        if (err instanceof Error) {
            realErr = err;
        }
        else if (typeof err === 'string') {
            realErr = new Error(`(string error: "${err}")`);
        }
        else if ((typeof err === 'undefined') || (err === null)) {
            realErr = new Error(`(unset error: "${err}")`);
        }
        else {
            realErr = new Error(`(unexpected error: "${err}")`);
        }
        return new ChainedError_1.ChainedError(realErr, this.format(message, ...opt));
    }
    newFailure(message, ...opt) {
        return Failable_1.Failable.newFailure(this.format(message, ...opt));
    }
    safeString(s, opts = DEFAULT_SAFE_STRING_OPTIONS) {
        try {
            if (typeof s === 'string') {
                const len = s.length;
                const maxlen = opts.longstringlen;
                if (len > maxlen) {
                    switch (opts.longstring) {
                        case 'truncate':
                            return `${s.slice(0, maxlen)}...)`;
                        case 'length':
                            return `<${len} char string>`;
                        case 'full':
                            return `${s}`;
                    }
                }
                return s;
            }
            if (typeof s === 'object') {
                if (s === null) {
                    return opts.null;
                }
                try {
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const thisCtx = this;
                    return JSON.stringify(s, (key, value) => {
                        if (typeof value === 'string') {
                            return thisCtx.safeString(value, opts);
                        }
                        return value;
                    }, opts.indent);
                }
                catch (jsonErr) {
                    const tName = s.constructor.name;
                    return `${tName}(...)`;
                }
            }
            return String(s);
        }
        catch (e) {
            try {
                const msg = this.safeErrorMessage(e);
                return `<error: ${msg}>`;
            }
            catch (ignoredErr) {
                return '<double error>';
            }
        }
    }
    format(message, ...opt) {
        return this.label + ': ' + [message, ...opt.map(s => this.safeString(s))].join(' ');
    }
    detailMsg(msg) {
        if (msg.length === 0) {
            return '';
        }
        return ': ' + msg.map(s => this.safeString(s)).join(' ');
    }
    assertUnreachable(...msg) {
        throw this.newError(`Should be unreachable: ${this.detailMsg(msg)}`);
    }
    assertLT(a, b, ...msg) {
        if (!(a < b)) {
            throw this.newError(`${a} < ${b}${this.detailMsg(msg)}`);
        }
    }
    assertLE(a, b, ...msg) {
        if (!(a <= b)) {
            throw this.newError(`${a} <= ${b}${this.detailMsg(msg)}`);
        }
    }
    assertGT(a, b, ...msg) {
        if (!(a > b)) {
            throw this.newError(`${a} > ${b}${this.detailMsg(msg)}`);
        }
    }
    assertGE(a, b, ...msg) {
        if (!(a >= b)) {
            throw this.newError(`${a} >= ${b}${this.detailMsg(msg)}`);
        }
    }
    assertEqual(a, b, ...msg) {
        if (!(a === b)) {
            const aStr = this.safeString(a);
            const bStr = this.safeString(b);
            throw this.newError(`${aStr} === ${bStr}${this.detailMsg(msg)}`);
        }
    }
    assertNotEqual(a, b, ...msg) {
        if (!(a !== b)) {
            throw this.newError(`${a} !== ${b}${this.detailMsg(msg)}`);
        }
    }
    assertOk(e, ...msg) {
        if (!(e)) {
            throw this.newError(`${e} is ok${this.detailMsg(msg)}`);
        }
    }
    assertTrue(e, ...msg) {
        if (!(e)) {
            throw this.newError(`must be true${this.detailMsg(msg)}`);
        }
    }
    assertFalse(e, ...msg) {
        if (e) {
            throw this.newError(`must be false${this.detailMsg(msg)}`);
        }
    }
    assertDefined(o, ...msg) {
        if (o === undefined) {
            throw this.newError(`${o} is not undefined${this.detailMsg(msg)}`);
        }
    }
    assertUndefined(o, ...msg) {
        if (o !== undefined) {
            const oMsg = this.safeString(o);
            throw this.newError(`${oMsg} is undefined${this.detailMsg(msg)}`);
        }
    }
    assertHasProperty(o, prop, ...msg) {
        if (!Object.prototype.hasOwnProperty.call(o, prop)) {
            const oMsg = this.safeString(o);
            throw this.newError(`${oMsg} must have "${prop}" property${this.detailMsg(msg)}`);
        }
    }
    assertNotZero(n, ...msg) {
        if (!(n !== 0)) {
            throw this.newError(`${n} !== 0${this.detailMsg(msg)}`);
        }
    }
    assertNotNull(n, ...msg) {
        if (n === null) {
            throw this.newError(`${n} !== null${this.detailMsg(msg)}`);
        }
    }
}
exports.Context = Context;
Context.ENABLE_TIMESTAMP_PREFIX = true;
//# sourceMappingURL=Context.js.map